import * as THREE from "three";
import { OrbitControls } from "three-stdlib";

// Ported from the Makistry ModelViewer seed (getCombinedBbox ~634-645, fitCamera
// ~1647-1683), stripped of React refs. Frames the visible geometry from a fixed
// front-top-right direction.

/** World-space AABB union of the given objects (respects transforms). */
export function combinedBbox(objects: THREE.Object3D[]): THREE.Box3 | null {
  if (objects.length === 0) return null;
  const box = new THREE.Box3();
  for (const o of objects) {
    if (!o.visible) continue;
    const b = new THREE.Box3().setFromObject(o);
    if (!b.isEmpty()) box.union(b);
  }
  return box.isEmpty() ? null : box;
}

export interface FitResult {
  center: THREE.Vector3;
  near: number;
  far: number;
}

/**
 * Position `camera` (perspective) so `bbox` fills the frame, and point `controls` at
 * the center. By default frames from the front-top-right dir (1,1,1). Pass
 * `preserveDir` to keep the CURRENT camera→target viewing direction instead — used so
 * an in-progress auto-rotate keeps its angle across a station reframe rather than
 * snapping back to the default view.
 */
export function fitCameraToBox(
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls,
  bbox: THREE.Box3,
  opts: { preserveDir?: boolean } = {}
): FitResult {
  const center = new THREE.Vector3();
  bbox.getCenter(center);
  const size = new THREE.Vector3();
  bbox.getSize(size);
  const maxDim = Math.max(size.x, size.y, size.z);

  const fov = camera.fov * (Math.PI / 180);
  const dist =
    Math.max(
      maxDim / (2 * Math.tan(fov / 2)),
      maxDim / (2 * Math.tan(fov / 2) * camera.aspect)
    ) * 2;

  let dir: THREE.Vector3;
  if (opts.preserveDir) {
    // Keep the current view angle (don't reset camera.up either).
    dir = camera.position.clone().sub(controls.target);
    if (dir.lengthSq() < 1e-9) dir.set(1, 1, 1);
    dir.normalize();
  } else {
    dir = new THREE.Vector3(1, 1, 1).normalize();
    camera.up.set(0, 1, 0);
  }
  camera.position.copy(dir.multiplyScalar(dist)).add(center);
  controls.target.copy(center);
  camera.lookAt(center);

  const near = dist / 100;
  const far = dist * 10;
  camera.near = near;
  camera.far = far;
  camera.updateProjectionMatrix();
  controls.update();

  return { center, near, far };
}
